import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTheme as useAppTheme } from "../context/ThemeContext";

/**
 * Owner portal MUI palette bridge.
 *
 * The owner screens are built out of MUI components that ask for
 * `color="primary"`, `bgcolor="primary.main"` and friends. Those resolve
 * against MUI's default blue, which is why picking a theme used to repaint the
 * sidebar and leave every dashboard card, chip and button behind.
 *
 * Feeding the chosen theme into a MUI palette fixes all of them at once, with
 * no page edits. Tenant, admin and the public pages keep MUI's default theme —
 * this only swaps in for a signed-in owner.
 *
 * The overrides below are deliberately restrained: softer radii, one quiet
 * border, a low shadow. The colour should read as the accent, not the paint.
 */
export default function OwnerMuiTheme({ children }) {
  const { theme: appTheme } = useAppTheme();
  const { validateUser, loginUser } = useSelector((state) => state);

  const isOwner =
    validateUser?.UserValidate?.data?.user?.type === "landlord" ||
    loginUser?.login?.data?.data?.type === "landlord";

  const muiTheme = useMemo(() => {
    if (!isOwner) return createTheme();

    return createTheme({
      palette: {
        primary: {
          main: appTheme.primary,
          dark: appTheme.primaryHover,
          light: `${appTheme.primary}1f`,
          contrastText: "#fff",
        },
        secondary: {
          main: appTheme.secondary,
          contrastText: "#10233b",
        },
        background: { default: "#f4f6fb" },
        text: { primary: "#16203a", secondary: "#5b6880" },
        divider: "rgba(18,32,58,0.09)",
      },
      shape: { borderRadius: 14 },
      typography: {
        button: { textTransform: "none", fontWeight: 600 },
        h4: { fontWeight: 700 },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 600 },
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: "none" },
            /* Elevated papers keep a soft shadow instead of MUI's grey stack */
            elevation1: { boxShadow: "0 10px 28px -20px rgba(18,32,58,0.55)" },
            elevation2: { boxShadow: "0 12px 30px -20px rgba(18,32,58,0.55)" },
            elevation3: { boxShadow: "0 14px 34px -20px rgba(18,32,58,0.55)" },
          },
        },
        MuiCard: {
          defaultProps: { elevation: 0 },
          styleOverrides: {
            root: {
              borderRadius: 16,
              border: "1px solid rgba(18,32,58,0.08)",
              boxShadow: "0 12px 30px -22px rgba(18,32,58,0.5)",
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: { borderRadius: 10 },
            containedPrimary: {
              /* A quiet tint of the theme colour, not a drop shadow */
              boxShadow: `0 10px 22px -14px ${appTheme.primary}`,
            },
          },
        },
        MuiChip: {
          styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
        },
        MuiTab: {
          styleOverrides: { root: { textTransform: "none", fontWeight: 600 } },
        },
        MuiLinearProgress: {
          styleOverrides: { root: { borderRadius: 999 } },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: { fontSize: 12, borderRadius: 8, padding: "6px 10px" },
          },
        },
      },
    });
  }, [appTheme, isOwner]);

  return <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>;
}
